import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Button } from '../components/Button';
import { Skeleton } from '../components/Skeleton';

import { SessionCenterArea } from '../components/SessionCenterArea';
import { PackageManagerModal } from '../components/PackageManagerModal';
import {
  SkillSearchModal,
  InstalledSkill,
  loadInstalledSkills,
  saveInstalledSkills,
} from '../components/SkillSearchModal';
import { SkillTreeModal } from '../components/SkillTreeModal';
import { getTemplateSkill } from '../helpers/templateSkillMap';
import { CheckpointPreviewDialog } from '../components/CheckpointPreviewDialog';
import { AnnotationCanvas } from '../components/AnnotationCanvas';
import { CreateFileDialog } from '../components/CreateFileDialog';
import { ImportFilesDialog } from '../components/ImportFilesDialog';
import { useUserIntegrations, useConnectGitHub } from '../helpers/useUserSettings';
import { PermissionsPanel } from '../components/PermissionsPanel';
import { AiAutoExecuteToggle } from '../components/AiAutoExecuteToggle';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '../components/Sheet';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/Dialog';
import { ResizablePanelGroup, ResizablePanel, ResizableHandle } from '../components/Resizable';
import { SessionRightPanel, RightTab, PanelSide } from '../components/SessionRightPanel';
import { MessageSquareText, Home, Undo2 } from 'lucide-react';
import { SessionNavToolbar } from '../components/SessionNavToolbar';
import { useSession } from '../helpers/useSessionsApi';
import { useSessionFiles, useUpdateFile, useCreateFile } from '../helpers/useFilesApi';
import * as CheckpointsApi from '../helpers/useCheckpointsApi';
import { useBulkCreateFiles } from '../helpers/useBulkCreateFiles';
import { useCurrentAiActivity } from '../helpers/useAiActivityApi';
import { useAuth } from '../helpers/useAuth';
import { useFavoriteStatus, useToggleFavorite } from '../helpers/useFavoritesApi';
import { useAnnotations } from '../helpers/useAnnotationsApi';
import { useIsMobile } from '../helpers/useIsMobile';
import { AlternativeRuntimeProvider } from '../helpers/AlternativeRuntimeProvider';
import { useSessionAiParticipants } from '../helpers/useSessionAiParticipants';
import { Selectable } from 'kysely';
import { SessionFiles } from '../helpers/schema';
import { OnboardingTutorial, useShowOnboarding } from '../components/OnboardingTutorial';
import styles from './session.$sessionId.module.css';

interface SessionPageContentProps {
  sessionId: string;
  session: any;
  files: Selectable<SessionFiles>[];
  isFilesLoading: boolean;
}

function SessionPageContent({
  sessionId,
  session,
  files,
  isFilesLoading,
}: SessionPageContentProps) {
  const isDev = import.meta.env.DEV;
  const navigate = useNavigate();
  const { authState } = useAuth();
  const isMobile = useIsMobile();
  const isNarrowDesktop =
    typeof window !== 'undefined' ? window.innerWidth <= 1320 : false;
  const updateFile = useUpdateFile();
  const createFile = useCreateFile();
  const bulkCreateFiles = useBulkCreateFiles();
  // Ref for SessionCenterArea to expose preview screenshot trigger to page-level handlers/effects.
  const screenshotTriggerRef = React.useRef<(() => void) | null>(null);

  // GitHub integration status
  const { data: integrationsData } = useUserIntegrations();
  const { connect: connectGitHub } = useConnectGitHub();
  const githubConnected =
    integrationsData?.integrations?.some((i) => i.provider === 'github') || false;

  const [activeFileId, setActiveFileId] = useState<string | null>(null);
  const [isCreateFileOpen, setIsCreateFileOpen] = useState(false);
  const [isImportFilesOpen, setIsImportFilesOpen] = useState(false);
  const [isPermissionsOpen, setIsPermissionsOpen] = useState(false);
  const [isSynced, setIsSynced] = useState(false);

  // Package manager modal and queue state
  const [isPackageManagerOpen, setIsPackageManagerOpen] = useState(false);
  const [packageQueue, setPackageQueue] = useState<string[]>([]);
  const [favorites, setFavorites] = useState<string[]>([]); // TODO: Load/save favorites persistently
  const [isInstalling, setIsInstalling] = useState(false);

  // Skill search modal state
  const [isSkillSearchOpen, setIsSkillSearchOpen] = useState(false);
  const [skillSearchQuery, setSkillSearchQuery] = useState('');
  const [isSkillTreeOpen, setIsSkillTreeOpen] = useState(false);
  const [skillTreeMode, setSkillTreeMode] = useState<'view' | 'create'>('view');
  const [activeSkills, setActiveSkills] = useState<InstalledSkill[]>(() =>
    loadInstalledSkills().filter((s) => s.active),
  );

  // AI participants (for skill tree assignment)
  const { data: aiParticipants } = useSessionAiParticipants(isSkillTreeOpen ? sessionId : '');
  const activeParticipantsList = (aiParticipants?.filter((p) => p.isActive) || []).map((p) => ({
    personaId: p.personaId,
    personaName: p.personaName || 'AI Participant',
    personaIcon: p.personaIcon,
  }));

  // Element editing state
  const [isInspectMode, setIsInspectMode] = useState(false);
  const [pendingElementEdit, setPendingElementEdit] = useState<any>(null);
  const lastSuggestionKeyRef = useRef<string | null>(null);

  // Update active skills when toggled (global active flag already saved by AiChatInput)
  const handleActiveSkillsChange = React.useCallback((skills: InstalledSkill[]) => {
    setActiveSkills(skills);
  }, []);

  // Toggle inspect mode (for element editing with AI)
  const handleInspect = React.useCallback(() => {
    setIsInspectMode((prev) => !prev);
  }, []);

  // Auto-activate template skill when session loads with a template
  useEffect(() => {
    try {
      const template = localStorage.getItem(`session_template_${sessionId}`);
      if (!template) return;

      const templateSkill = getTemplateSkill(template);
      if (!templateSkill) return;

      const allSkills = loadInstalledSkills();
      const alreadyInstalled = allSkills.find((s) => s.name === templateSkill.name);

      if (alreadyInstalled) {
        // Ensure it's globally active
        if (!alreadyInstalled.active) {
          const updatedSkills = allSkills.map((s) =>
            s.name === alreadyInstalled.name ? { ...s, active: true } : s,
          );
          saveInstalledSkills(updatedSkills);
          setActiveSkills(updatedSkills.filter((s) => s.active));
        }
        return;
      }

      // Not installed yet — fetch SKILL.md and install
      const installSkill = async () => {
        try {
          const res = await fetch(templateSkill.skillMdUrl);
          const content = res.ok ? await res.text() : undefined;

          const newSkill: InstalledSkill = {
            name: templateSkill.name,
            fullName: templateSkill.repo,
            description: templateSkill.description,
            url: `https://github.com/${templateSkill.repo}`,
            active: true,
            installedAt: new Date().toISOString(),
            content: content || undefined,
          };

          const updatedSkills = [...allSkills, newSkill];
          saveInstalledSkills(updatedSkills);
          setActiveSkills(updatedSkills.filter((s) => s.active));
          if (isDev) console.log(`[Session] Auto-installed template skill: ${templateSkill.name}`);
        } catch (err) {
          console.warn(
            `[Session] Failed to auto-install template skill ${templateSkill.name}:`,
            err,
          );
        }
      };
      installSkill();
    } catch (e) {
      // Ignore errors in auto-skill activation
    }
  }, [sessionId]);

  // ── Element edit mode: Listen for element selection from preview inspector ──
  useEffect(() => {
    const handleElementEditRequest = (e: any) => {
      const { element, action, imageData, elementContext, sessionId: eventSessionId } = e.detail;

      if (eventSessionId !== sessionId) return; // Only handle events from current session

      // Build the message to send to AI
      const message = action === 'upload'
        ? `${elementContext}\n\nI've uploaded an image. Please update this element to use/display the uploaded image.`
        : `${elementContext}\n\nPlease regenerate/redesign this element to look better or be more modern.`;

      // Send to AI chat — auto-send for regen, manual for upload
      window.dispatchEvent(new CustomEvent('tasking.prefillChat', {
        detail: {
          message: message,
          autoSend: action === 'regenerate',
          files: imageData ? [{
            name: `element_${element.tag}_${Date.now()}.png`,
            dataUrl: imageData,
            type: 'image/png',
          }] : [],
        },
      }));

      // Scroll to chat panel if needed
      const chatPanel = document.querySelector('[data-chat-panel]');
      if (chatPanel) {
        chatPanel.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }
    };

    window.addEventListener('tasking.elementEditRequest', handleElementEditRequest);
    return () => window.removeEventListener('tasking.elementEditRequest', handleElementEditRequest);
  }, [sessionId]);

  // Listen for screenshot requests from AI chat (tasking.requestScreenshot event)
  useEffect(() => {
    const handleScreenshotRequest = (e: any) => {
      if (screenshotTriggerRef.current) {
        screenshotTriggerRef.current();
      }
    };

    window.addEventListener('tasking.requestScreenshot', handleScreenshotRequest);
    return () => window.removeEventListener('tasking.requestScreenshot', handleScreenshotRequest);
  }, []);

  // Listen for element edit suggestions from AI chat
  useEffect(() => {
    const handleElementEditSuggestion = async (e: any) => {
      const { filename, code, sessionId: eventSessionId } = e.detail;

      if (eventSessionId !== sessionId) return; // Only handle for current session

      // Find the file being suggested
      const targetFile = files.find((f) => {
        const fullPath = f.filePath === '/' ? `/${f.fileName}` : `${f.filePath}/${f.fileName}`;
        return fullPath === filename || f.fileName === filename;
      });

      if (targetFile) {
        const suggestionKey = `${targetFile.filePath || '/'}:${filename}:${code}`;
        if (lastSuggestionKeyRef.current === suggestionKey) return;
        lastSuggestionKeyRef.current = suggestionKey;
        // Auto-apply changes immediately without confirmation dialog
        try {
          await updateFile.mutateAsync({
            id: targetFile.id,
            fileName: targetFile.fileName,
            content: code,
          });
          toast.success('Element changes applied!');
        } catch (err: any) {
          console.error('Failed to auto-apply changes:', err);
          toast.error(`Failed to apply changes: ${err?.message || 'Unknown error'}`);
        }
      }
    };

    window.addEventListener('tasking.elementEditSuggestion', handleElementEditSuggestion);
    return () => window.removeEventListener('tasking.elementEditSuggestion', handleElementEditSuggestion);
  }, [sessionId, files, updateFile]);

  // Compute installed packages from package.json in files
  const installedPackages = React.useMemo(() => {
    const pkgFile = files.find(
      (f) =>
        f.fileName === 'package.json' && (f.filePath === '/' || f.filePath === '' || !f.filePath),
    );
    if (!pkgFile?.content) return {};
    try {
      const pkg = JSON.parse(pkgFile.content);
      return { ...pkg.dependencies, ...pkg.devDependencies } as Record<string, string>;
    } catch {
      return {};
    }
  }, [files]);

  // Add package to queue
  const handleAddToQueue = (pkg: string) => {
    setPackageQueue((prev) => (prev.includes(pkg) ? prev : [...prev, pkg]));
  };

  // Remove package from queue
  const handleRemoveFromQueue = (pkg: string) => {
    setPackageQueue((prev) => prev.filter((p) => p !== pkg));
  };

  // Toggle favorite
  const handleToggleFavorite = (pkg: string) => {
    setFavorites((prev) => (prev.includes(pkg) ? prev.filter((p) => p !== pkg) : [...prev, pkg]));
  };

  // Install next package in queue (updates package.json in D1 only)
  useEffect(() => {
    const installNext = async () => {
      if (!isInstalling && packageQueue.length > 0) {
        setIsInstalling(true);
        const pkg = packageQueue[0];
        try {
          // Find package.json file
          let pkgFile = files.find(
            (f) =>
              f.fileName === 'package.json' &&
              (f.filePath === '/' || f.filePath === '' || !f.filePath),
          );

          // Auto-create package.json if it doesn't exist
          if (!pkgFile) {
            const newPkgJson = {
              name: session.name?.toLowerCase().replace(/\s+/g, '-') || 'project',
              version: '1.0.0',
              description: '',
              main: 'index.js',
              scripts: { start: 'node index.js', dev: 'node index.js' },
              dependencies: {},
              devDependencies: {},
            };

            try {
              const createdFile = await createFile.mutateAsync({
                sessionId,
                fileName: 'package.json',
                filePath: '/',
                language: 'json',
                content: JSON.stringify(newPkgJson, null, 2),
              });

              pkgFile = {
                id: createdFile.file.id,
                fileName: 'package.json',
                filePath: '/',
                content: JSON.stringify(newPkgJson, null, 2),
                language: 'json',
                sessionId: sessionId,
                createdAt: new Date().toISOString(),
                updatedAt: new Date().toISOString(),
              } as Selectable<SessionFiles>;

              toast.success('Created package.json automatically');
            } catch (createErr: any) {
              console.error('[PackageManager] Failed to create package.json:', createErr);
              toast.error(
                `Failed to create package.json: ${createErr?.message || String(createErr)}`,
              );
              setPackageQueue((prev) => prev.slice(1));
              setIsInstalling(false);
              return;
            }
          }

          // Update package.json in D1
          let pkgJson: any = {};
          try {
            pkgJson = JSON.parse(pkgFile.content || '{}');
          } catch (e) {
            console.error('Failed to parse package.json:', e);
            pkgJson = {};
          }

          // Parse package name (handle "pkg@version" format)
          const pkgNameOnly =
            pkg.includes('@') && !pkg.startsWith('@')
              ? pkg.split('@')[0]
              : pkg.startsWith('@')
                ? pkg.split('@').slice(0, 2).join('@')
                : pkg;

          pkgJson.dependencies = pkgJson.dependencies || {};
          pkgJson.dependencies[pkgNameOnly] = 'latest';

          const updatedContent = JSON.stringify(pkgJson, null, 2);
          await updateFile.mutateAsync({
            id: pkgFile.id,
            fileName: pkgFile.fileName,
            content: updatedContent,
          });

          toast.success(`Added ${pkg} to package.json`);
        } catch (err: any) {
          console.error(`Failed to add ${pkg}:`, err);
          toast.error(`Failed to add ${pkg}: ${err?.message || String(err)}`);
        }
        setPackageQueue((prev) => prev.slice(1));
        setIsInstalling(false);
      }
    };
    installNext();
  }, [packageQueue, isInstalling, files, updateFile, createFile, sessionId, session.name]);
  const [isRightPanelOpen, setIsRightPanelOpen] = useState(true);
  const [activeRightTab, setActiveRightTab] = useState<RightTab>('ai');
  // Initialize to 'right' to match server rendering, then read from localStorage in useEffect
  const [panelSide, setPanelSide] = useState<PanelSide>('right');

  // Load panelSide from localStorage after mount to avoid hydration mismatch
  useEffect(() => {
    if (typeof window !== 'undefined') {
      const savedSide = localStorage.getItem('tasking_panel_side') as PanelSide;
      if (savedSide) {
        setPanelSide(savedSide);
      }
    }
  }, []);

  const handlePanelSideToggle = () => {
    const newSide = panelSide === 'left' ? 'right' : 'left';
    setPanelSide(newSide);
    if (typeof window !== 'undefined') {
      localStorage.setItem('tasking_panel_side', newSide);
    }
  };

  // Annotation state
  const [isAnnotating, setIsAnnotating] = useState(false);
  const [previewScreenshot, setPreviewScreenshot] = useState<string | null>(null);
  const [initialAttachment, setInitialAttachment] = useState<{
    content: string;
    name?: string;
    textNotes?: string[];
  } | null>(null);
  const [previewCheckpointId, setPreviewCheckpointId] = useState<string | null>(null);

  const { data: annotationsData } = useAnnotations(activeRightTab === 'ai' ? sessionId : undefined);
  const { isFavorited } = useFavoriteStatus(sessionId);
  const toggleFavoriteMutation = useToggleFavorite();

  const currentUser = authState.type === 'authenticated' ? authState.user : null;
  const isOwner = currentUser && session && currentUser.id === session.ownerId;

  // Removed duplicate handleToggleFavorite for session favorite mutation

  // Mark files as synced once they finish loading from D1
  useEffect(() => {
    if (!isFilesLoading && !isSynced) {
      setIsSynced(true);
    }
  }, [isFilesLoading, isSynced]);

  // Reset sync state when session changes
  useEffect(() => {
    setIsSynced(false);
  }, [sessionId]);

  // Template bootstrap: consume template key from localStorage on new sessions
  // Template starter files are now created server-side or via AI chat
  useEffect(() => {
    if (!sessionId) return;
    const key = `session_template_${sessionId}`;
    const tpl = localStorage.getItem(key);
    if (tpl) {
      localStorage.removeItem(key);
      if (isDev) console.log('[session] Consumed template key from localStorage:', tpl);
    }
  }, [sessionId]);

  // Set initial active file if none selected and files exist
  // BUT only if files have been stable for a moment (not during initial creation)
  useEffect(() => {
    if (!activeFileId && files.length > 0) {
      // Delay auto-selection slightly to avoid selecting during file creation
      const timer = setTimeout(() => {
        // Only auto-select if still no file is selected (user hasn't manually selected)
        setActiveFileId((prev) => prev || files[0]?.id || null);
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [files.length, activeFileId]); // Only depend on files.length, not files array itself

  // If active file is deleted, switch to another or null
  useEffect(() => {
    if (activeFileId && files.length > 0 && !files.find((f) => f.id === activeFileId)) {
      setActiveFileId(files[0].id);
    } else if (activeFileId && files.length === 0) {
      setActiveFileId(null);
    }
  }, [files, activeFileId]);

  // Close right panel on mobile by default
  useEffect(() => {
    if (isMobile) {
      setIsRightPanelOpen(false);
    }
  }, [isMobile]);

  // Force body to not scroll on mobile when in session
  useEffect(() => {
    if (isMobile) {
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = '';
      };
    }
  }, [isMobile]);

  const activeFile = files.find((f) => f.id === activeFileId);

  // Debugging: log active file id and activeFile summary
  useEffect(() => {
    if (!isDev) return;
    try {
      console.log(
        '[SessionPageContent] activeFileId=',
        activeFileId,
        'activeFilePath=',
        activeFile?.filePath,
        'filesCount=',
        files.length,
      );
      if (activeFile)
        console.log(
          '[SessionPageContent] activeFile preview=',
          (activeFile.content || '').slice(0, 200),
        );
    } catch (e) {
      /* ignore */
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeFileId, files.length, isDev]);

  const handleInsertCode = async (code: string) => {
    if (!activeFile) return;

    try {
      const newContent = activeFile.content ? `${activeFile.content}\n\n${code}` : code;

      await updateFile.mutateAsync({
        id: activeFile.id,
        content: newContent,
      });

      toast.success('Code appended to file');
    } catch (err) {
      toast.error('Failed to update file');
    }
  };

  const handleScreenshot = (dataUrl: string) => {
    setPreviewScreenshot(dataUrl);
    // Automatically open AI chat panel and switch to it
    setActiveRightTab('ai');
    setIsRightPanelOpen(true);
    toast.success('Screenshot captured - ready to send to AI');
  };

  // Expose screenshot trigger function for AI chat and element editing
  useEffect(() => {
    const triggerScreenshot = () => {
      if (screenshotTriggerRef.current) {
        screenshotTriggerRef.current();
      }
    };

    // Store in window.taskingTech namespace for AI chat access
    if (typeof window !== 'undefined') {
      (window as any).taskingTech = (window as any).taskingTech || {};
      (window as any).taskingTech.takeScreenshot = triggerScreenshot;
    }

    return () => {
      if (typeof window !== 'undefined' && (window as any).taskingTech) {
        delete (window as any).taskingTech.takeScreenshot;
      }
    };
  }, [screenshotTriggerRef]);

  // Receive annotated image from inline annotation overlay (snipping tool)
  const handleAnnotate = (annotatedImageDataUrl: string, textNotes?: string[]) => {
    setInitialAttachment({ content: annotatedImageDataUrl, name: 'annotation.png', textNotes });
    setActiveRightTab('ai');
    setIsRightPanelOpen(true);
    toast.success('Annotation saved — ready to send to AI');
  };

  const handleOpenAnnotation = () => {
    if (!previewScreenshot) {
      toast.error('Take a screenshot first');
      return;
    }
    setIsAnnotating(true);
  };

  const handleAnnotationSave = (annotationData: any) => {
    setIsAnnotating(false);
    const annotatedImage = annotationData?.annotatedImage || null;
    if (annotatedImage) {
      // Extract text notes from annotation data so AI can read them
      const textNotes: string[] = [];
      if (annotationData.description?.trim()) {
        textNotes.push(annotationData.description.trim());
      }
      if (annotationData.annotationData?.textNotes?.length) {
        for (const note of annotationData.annotationData.textNotes) {
          if (note.text?.trim()) textNotes.push(note.text.trim());
        }
      }
      setInitialAttachment({ content: annotatedImage, name: 'annotation.png', textNotes });
    }
    // Automatically open AI chat panel with the annotation
    setActiveRightTab('ai');
    setIsRightPanelOpen(true);
    toast.success('Annotation saved - ready to send to AI');
  };

  // Annotate an existing attachment (e.g. sent image in chat)
  const handleAnnotateAttachment = (dataUrl: string) => {
    setPreviewScreenshot(dataUrl);
    setIsAnnotating(true);
    setActiveRightTab('ai');
    setIsRightPanelOpen(true);
    toast.success('Attachment ready for annotation');
  };

  const handleAttachmentSent = () => {
    setInitialAttachment(null);
  };

  // Lifted auto-execute toggle state so it can be rendered in the status bar
  const [autoExecute, setAutoExecute] = useState<boolean>(true);

  // Onboarding tutorial state
  const showOnboarding = useShowOnboarding(sessionId, currentUser?.id ?? null);
  const [onboardingComplete, setOnboardingComplete] = useState(false);

  // Ref to expose AI chat sendMessage function for auto-generation
  const aiSendMessageRef = React.useRef<((message: string) => void) | null>(null);
  const autoPromptTriggeredRef = React.useRef(false);

  // Auto-generate project ONLY when user explicitly provided a description
  // If only a session name was given (no description), let the user pick framework and type first message
  useEffect(() => {
    if (autoPromptTriggeredRef.current) return;
    if (!sessionId) return;
    if (!isSynced) return;
    // Wait for onboarding to complete before auto-generating
    if (showOnboarding && !onboardingComplete) return;

    try {
      const promptKey = `session_auto_prompt_${sessionId}`;
      const nameKey = `session_name_${sessionId}`;
      const autoPrompt = localStorage.getItem(promptKey);

      // Only auto-send if user explicitly entered a description
      // Session name alone should NOT trigger auto-generation — let user pick framework first
      if (autoPrompt) {
        autoPromptTriggeredRef.current = true;

        // Wait a bit for the AI panel to be ready
        setTimeout(() => {
          if (aiSendMessageRef.current) {
            const prompt = `Create a complete project based on this description: ${autoPrompt}`;

            if (isDev) console.log('[session] Auto-triggering AI generation:', prompt);
            aiSendMessageRef.current(prompt);
          }
        }, 1500); // Give time for AI panel to initialize
      }

      // Always clean up stored prompts so they don't linger
      try {
        localStorage.removeItem(promptKey);
        localStorage.removeItem(nameKey);
      } catch (e) {
        /* ignore */
      }
    } catch (e) {
      console.error('[session] Error in auto-generation:', e);
    }
  }, [sessionId, isSynced, showOnboarding, onboardingComplete, isDev]);

  // Handle "Try to fix" button clicks from LivePreview/Terminal
  const handleTryFix = React.useCallback(
    (errorContext: string) => {
      if (aiSendMessageRef.current) {
        const rawError = String(errorContext || '');
        const lines = rawError
          .split('\n')
          .map((l) => l.trim())
          .filter(Boolean);

        // Parse errors from multiple formats:
        // 1. esbuild: virtual-fs:/path:line:col: ERROR: message
        // 2. Vite: Failed to resolve import "X" from "path"
        // 3. Vite: /path:line:col syntax error / transform error
        // 4. Generic: GET /path 500 (Internal Server Error)
        type ParsedError = { file: string; line: number; message: string };
        const parsedErrors: ParsedError[] = [];
        const mentionedFiles = new Set<string>();

        for (const line of lines) {
          // esbuild virtual-fs format
          const esbuildMatch = line.match(/^virtual-fs:(\/[^:]+):(\d+):(\d+):\s*ERROR:\s*(.+)$/);
          if (esbuildMatch) {
            parsedErrors.push({ file: esbuildMatch[1], line: parseInt(esbuildMatch[2], 10), message: esbuildMatch[4] });
            mentionedFiles.add(esbuildMatch[1]);
            continue;
          }
          // Vite import resolution: Failed to resolve import "X" from "path.tsx"
          const viteImportMatch = line.match(/Failed to resolve import\s+"([^"]+)"\s+from\s+"([^"]+)"/);
          if (viteImportMatch) {
            parsedErrors.push({ file: viteImportMatch[2], line: 0, message: `Failed to resolve import "${viteImportMatch[1]}"` });
            mentionedFiles.add(viteImportMatch[2]);
            continue;
          }
          // Vite transform error: /src/App.tsx:42:5 or Internal server error: /src/App.tsx
          const vitePathMatch = line.match(/(?:error|Error)[:\s]+\/?([^\s:]+\.[tj]sx?)/);
          if (vitePathMatch) {
            mentionedFiles.add(vitePathMatch[1]);
          }
          // HTTP 500 on a source file: GET /src/App.tsx 500
          const http500Match = line.match(/GET\s+\/?([^\s]+\.[tj]sx?)\s+(?:net::ERR|500)/);
          if (http500Match) {
            mentionedFiles.add(http500Match[1]);
          }
        }

        const uniqueFiles = Array.from(mentionedFiles);
        const sampleMessages = Array.from(new Set(parsedErrors.map((e) => e.message))).slice(0, 4);

        // Helper to find a session file by path
        const findFile = (filePath: string) => {
          const normalizedPath = filePath.replace(/^\/+/, '');
          return files.find((f) => {
            const fp = (
              (f.filePath || '/').replace(/\/+$/, '') + '/' + f.fileName
            ).replace(/\/+/g, '/').replace(/^\/+/, '');
            return fp === normalizedPath || f.fileName === normalizedPath;
          });
        };

        // Build file snippets — if we identified specific files, include those;
        // otherwise include ALL session files so the AI can actually diagnose
        const fileSnippets: string[] = [];
        const filesToInclude = uniqueFiles.length > 0
          ? uniqueFiles.slice(0, 6)
          : files
              .filter((f) => f.content && /\.[tj]sx?$/.test(f.fileName))
              .slice(0, 8)
              .map((f) => {
                const fp = ((f.filePath || '/').replace(/\/+$/, '') + '/' + f.fileName)
                  .replace(/\/+/g, '/').replace(/^\/+/, '');
                return fp;
              });

        for (const filePath of filesToInclude) {
          const matchingFile = findFile(filePath);
          if (matchingFile?.content) {
            const errorsInFile = parsedErrors.filter((e) => e.file === filePath);
            const errorLines = errorsInFile.length > 0
              ? `Errors:\n${errorsInFile.map((e) => `  Line ${e.line}: ${e.message}`).join('\n')}\n\n`
              : '';
            fileSnippets.push(
              `\n--- ${filePath} ---\n${errorLines}\`\`\`tsx\n${matchingFile.content}\n\`\`\``
            );
          }
        }

        const summaryLine = parsedErrors.length > 0
          ? `Preview build failed with ${parsedErrors.length} error(s) across ${uniqueFiles.length} file(s).`
          : `Preview build failed. Error: ${rawError.substring(0, 300)}`;
        const detailLine = uniqueFiles.length > 0
          ? `Main files: ${uniqueFiles.slice(0, 6).join(', ')}`
          : '';
        const messageLine = sampleMessages.length > 0
          ? `Likely issues: ${sampleMessages.join(' | ')}`
          : '';

        const fixPrompt = [
          'Fix the syntax error(s) in the file(s) below. Do NOT regenerate from scratch — find and fix the specific broken syntax.',
          summaryLine,
          detailLine,
          messageLine,
          ...fileSnippets,
          '',
          'Output ONLY the fixed file(s). Keep all existing code, just fix the syntax error(s).',
        ]
          .filter(Boolean)
          .join('\n');
        aiSendMessageRef.current(fixPrompt);
        // Ensure AI panel is visible
        if (!isRightPanelOpen) {
          setIsRightPanelOpen(true);
        }
        setActiveRightTab('chat');
      }
    },
    [isRightPanelOpen, files],
  );

  // Handle "AI Generate" from terminal: open chat panel so user can type what to generate
  const handleAiGenerate = React.useCallback(() => {
    if (!isRightPanelOpen) {
      setIsRightPanelOpen(true);
    }
    setActiveRightTab('chat');
  }, []);


  const rightPanel = (
    <SessionRightPanel
      activeTab={activeRightTab}
      onTabChange={setActiveRightTab}
      sessionId={sessionId}
      sessionName={session.name}
      sessionStatus={session.status}
      templateId={session.templateId}
      activeFile={activeFile}
      annotations={annotationsData?.annotations || []}
      isOwner={!!isOwner}
      isFavorited={isFavorited}
      onToggleFavorite={() => toggleFavoriteMutation.mutate({ sessionId })}
      onInsertCode={handleInsertCode}
      onPreviewCheckpoint={setPreviewCheckpointId}
      onOpenPermissions={() => setIsPermissionsOpen(true)}
      onOpenImport={isOwner ? () => setIsImportFilesOpen(true) : undefined}
      onOpenAnnotation={handleOpenAnnotation}
      onRequestAnnotateAttachment={handleAnnotateAttachment}
      panelSide={panelSide}
      onToggleSide={handlePanelSideToggle}
      onClose={() => setIsRightPanelOpen(false)}
      sessionFiles={files}
      initialAttachment={initialAttachment}
      onAttachmentSent={handleAttachmentSent}
      autoExecute={autoExecute}
      onAutoExecuteChange={setAutoExecute}
      githubConnected={githubConnected}
      onGithubConnect={connectGitHub}
      onSendMessageRef={React.useCallback((sendFn: ((message: string) => void) | null) => {
        aiSendMessageRef.current = sendFn;
      }, [])}
      onOpenSkillSearch={React.useCallback((query?: string) => {
        setSkillSearchQuery(query || '');
        setIsSkillSearchOpen(true);
      }, [])}
      onOpenSkillTree={React.useCallback((mode?: 'view' | 'create') => {
        setSkillTreeMode(mode || 'view');
        setIsSkillTreeOpen(true);
      }, [])}
      activeSkills={activeSkills}
      onActiveSkillsChange={handleActiveSkillsChange}
      onRequestScreenshot={() => {
        if (screenshotTriggerRef.current) {
          screenshotTriggerRef.current();
        }
      }}
      previewScreenshot={previewScreenshot}
    />
  );

  // Listen for file open requests emitted from chat UI (e.g. code change pills)
  const restoreFileMutation = CheckpointsApi.useRestoreCheckpointFile();
  const restoreCheckpointMutation = CheckpointsApi.useRestoreCheckpoint();

  useEffect(() => {
    const handler = (e: any) => {
      const filename = e?.detail?.filename;
      if (!filename) return;

      // Prefer exact path matches first; fallback to filename-only matching.
      const normalizedRequested = String(filename)
        .trim()
        .replace(/\\/g, '/')
        .replace(/\/+/g, '/')
        .replace(/^\.\//, '')
        .replace(/^\//, '')
        .toLowerCase();

      const fileWithPath = files.map((f) => {
        const normalizedFilePath =
          f.filePath === '/' || !f.filePath ? '' : String(f.filePath).replace(/\/$/, '');
        const fullPath = `${normalizedFilePath}/${f.fileName}`.replace(/^\/+/, '');
        return { file: f, fullPath, normalizedFullPath: fullPath.toLowerCase() };
      });

      let match = fileWithPath.find((fp) => fp.normalizedFullPath === normalizedRequested)?.file;

      if (!match) {
        const basenameMatches = fileWithPath.filter(
          (fp) => fp.file.fileName.toLowerCase() === normalizedRequested,
        );
        if (basenameMatches.length === 1) {
          match = basenameMatches[0].file;
        } else if (basenameMatches.length > 1 && activeFile?.filePath) {
          // If duplicate names exist, prefer the same directory as current active file.
          const sameDir = basenameMatches.find((fp) => fp.file.filePath === activeFile.filePath);
          match = (sameDir || basenameMatches[0]).file;
        } else if (basenameMatches.length > 1) {
          match = basenameMatches[0].file;
        }
      }

      if (match) {
        setActiveFileId(match.id);
      } else {
        toast.error(`File not found: ${filename}`);
      }
    };

    const revertHandler = (e: any) => {
      const filename = e?.detail?.filename;
      const checkpointId = e?.detail?.checkpointId;
      if (!filename || !checkpointId) return;
      try {
        restoreFileMutation.mutate({ checkpointId, filename });
      } catch (err) {
        console.error('Restore mutation failed call:', err);
        toast.error('Failed to restore file');
      }
    };

    // Redo handler: re-apply AI-generated code to a file
    const redoHandler = (e: any) => {
      const filename = e?.detail?.filename;
      const code = e?.detail?.code;
      if (!filename || !code) return;
      // Find the file by matching filename
      const target = files?.find(
        (f: any) =>
          f.fileName === filename || `${f.filePath || ''}${f.fileName || ''}` === filename,
      );
      if (target) {
        try {
          updateFile.mutate({ id: target.id, content: code });
          toast.success(`Re-applied AI changes to ${filename}`);
        } catch (err) {
          console.error('Redo mutation failed:', err);
          toast.error('Failed to re-apply changes');
        }
      } else {
        toast.error(`Could not find file ${filename} to re-apply changes`);
      }
    };

    window.addEventListener('tasking.openFile', handler as EventListener);
    window.addEventListener('tasking.revertFile', revertHandler as EventListener);
    window.addEventListener('tasking.redoFile', redoHandler as EventListener);
    return () => {
      window.removeEventListener('tasking.openFile', handler as EventListener);
      window.removeEventListener('tasking.revertFile', revertHandler as EventListener);
      window.removeEventListener('tasking.redoFile', redoHandler as EventListener);
    };
  }, [files, restoreFileMutation, updateFile, activeFile?.filePath]);

  // Track last auto-created checkpoint to surface undo/view UI
  const [lastAutoCheckpoint, setLastAutoCheckpoint] = useState<{
    checkpointId: string;
    createdFiles: string[];
    updatedFiles: string[];
  } | null>(null);

  useEffect(() => {
    const handler = (e: any) => {
      const detail = e?.detail || {};
      const { checkpointId, createdFiles = [], updatedFiles = [] } = detail;
      if (!checkpointId) return;
      setLastAutoCheckpoint({ checkpointId, createdFiles, updatedFiles });
    };
    window.addEventListener('tasking.applyCheckpoint', handler as EventListener);
    return () => window.removeEventListener('tasking.applyCheckpoint', handler as EventListener);
  }, []);

  // Open git tree tab when toolbar icon clicked
  useEffect(() => {
    const handler = () => setActiveRightTab('git');
    window.addEventListener('tasking.openGitTree', handler);
    return () => window.removeEventListener('tasking.openGitTree', handler);
  }, []);

  // Open AI chat panel when triggered from global navigation.
  useEffect(() => {
    const handler = () => {
      setActiveRightTab('ai');
      setIsRightPanelOpen(true);
    };
    window.addEventListener('tasking.openAiChat', handler);
    return () => window.removeEventListener('tasking.openAiChat', handler);
  }, []);

  const mainContent = (
    <div className={styles.mainArea}>

      <SessionCenterArea
        sessionId={sessionId}
        sessionName={session.name}
        activeFile={activeFile}
        activeFileId={activeFileId}
        files={files}
        currentUser={currentUser}
        isOwner={!!isOwner}
        onScreenshot={handleScreenshot}
        onAnnotate={handleAnnotate}
        onSelectFile={setActiveFileId}
        onAddFile={() => setIsCreateFileOpen(true)}
        onImport={isOwner ? () => setIsImportFilesOpen(true) : undefined}
        onTryFix={handleTryFix}
        onOpenPackageManager={() => setIsPackageManagerOpen(true)}
        onAiGenerate={handleAiGenerate}
        isInspectMode={isInspectMode}
        onInspect={handleInspect}
        screenshotTriggerRef={screenshotTriggerRef}
      />

      <PackageManagerModal
        open={isPackageManagerOpen}
        onClose={() => setIsPackageManagerOpen(false)}
        onInstall={handleAddToQueue}
        favorites={favorites}
        onToggleFavorite={handleToggleFavorite}
        queue={packageQueue}
        onRemoveFromQueue={handleRemoveFromQueue}
        installedPackages={installedPackages}
      />
      <SkillSearchModal
        open={isSkillSearchOpen}
        onClose={() => setIsSkillSearchOpen(false)}
        onInstallSkill={(command) => {
          // Send the install command to AI chat
          if (aiSendMessageRef.current) {
            aiSendMessageRef.current(command);
          }
          setIsSkillSearchOpen(false);
        }}
        initialQuery={skillSearchQuery}
        onActiveSkillsChange={handleActiveSkillsChange}
        searchEndpoint="/_api/skills/search"
      />
      <SkillTreeModal
        open={isSkillTreeOpen}
        onClose={() => setIsSkillTreeOpen(false)}
        sessionId={sessionId}
        initialMode={skillTreeMode}
        participants={activeParticipantsList}
      />
    </div>
  );

  return (
    <div className={`${styles.container} ${isMobile ? styles.mobileContainer : ''}`}>
      <title>{session.name} | Tasking.tech</title>

      {/* Onboarding Tutorial - shows during loading so users learn while waiting */}
      {showOnboarding && !onboardingComplete && (
        <OnboardingTutorial
          sessionId={sessionId}
          userId={currentUser?.id ?? null}
          type="session"
          isLoading={false}
          onComplete={() => setOnboardingComplete(true)}
        />
      )}

      <div className={styles.workspace}>
        {/* Nav toolbar on opposite side of chat panel */}
        {!isMobile && panelSide === 'right' && <SessionNavToolbar side="left" />}

        <ResizablePanelGroup direction="horizontal" key={panelSide}>
          {panelSide === 'left' && isRightPanelOpen && !isMobile && (
            <>
              <ResizablePanel
                defaultSize={isNarrowDesktop ? 24 : 30}
                minSize={isNarrowDesktop ? 18 : 25}
              >
                {rightPanel}
              </ResizablePanel>
              <ResizableHandle />
            </>
          )}

          <ResizablePanel
            defaultSize={!isMobile && isRightPanelOpen ? (isNarrowDesktop ? 76 : 70) : 100}
            minSize={40}
          >
            {mainContent}
          </ResizablePanel>

          {panelSide === 'right' && isRightPanelOpen && !isMobile && (
            <>
              <ResizableHandle />
              <ResizablePanel
                defaultSize={isNarrowDesktop ? 24 : 30}
                minSize={isNarrowDesktop ? 18 : 25}
              >
                {rightPanel}
              </ResizablePanel>
            </>
          )}
        </ResizablePanelGroup>

        {/* Nav toolbar on opposite side of chat panel */}
        {!isMobile && panelSide === 'left' && <SessionNavToolbar side="right" />}
      </div>

      {/* Mobile Right Panel as Bottom Sheet */}
      {isMobile && (
        <>
          <Sheet open={isRightPanelOpen} onOpenChange={setIsRightPanelOpen}>
            <SheetContent side="bottom" className={styles.mobileRightSheet}>
              {rightPanel}
            </SheetContent>
          </Sheet>

          {!isRightPanelOpen && (
            <Button
              className={styles.mobileFab}
              size="icon-lg"
              onClick={() => setIsRightPanelOpen(true)}
            >
              <MessageSquareText size={24} />
            </Button>
          )}
        </>
      )}

      {isMobile && <SessionNavToolbar side="left" />}

      <CreateFileDialog
        sessionId={sessionId}
        open={isCreateFileOpen}
        onOpenChange={setIsCreateFileOpen}
      />

      <ImportFilesDialog
        sessionId={sessionId}
        open={isImportFilesOpen}
        onOpenChange={setIsImportFilesOpen}
      />

      <Sheet open={isPermissionsOpen} onOpenChange={setIsPermissionsOpen}>
        <SheetContent side="right" className={styles.permissionsSheet}>
          <SheetHeader>
            <SheetTitle>Access Control</SheetTitle>
          </SheetHeader>
          <div className={styles.permissionsPanelWrapper}>
            <PermissionsPanel sessionId={sessionId} />
          </div>
        </SheetContent>
      </Sheet>

      <CheckpointPreviewDialog
        checkpointId={previewCheckpointId}
        open={!!previewCheckpointId}
        onOpenChange={(open) => !open && setPreviewCheckpointId(null)}
      />

      <Dialog open={isAnnotating} onOpenChange={setIsAnnotating}>
        <DialogContent className={styles.annotationDialog}>
          <DialogHeader>
            <DialogTitle>Annotate Preview</DialogTitle>
          </DialogHeader>
          <div className={styles.annotationWrapper}>
            {previewScreenshot && (
              <AnnotationCanvas
                imageUrl={previewScreenshot}
                sessionId={sessionId}
                onSave={handleAnnotationSave}
                onCancel={() => setIsAnnotating(false)}
              />
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default function SessionPage() {
  const { sessionId } = useParams<{ sessionId: string }>();
  const navigate = useNavigate();

  const {
    data: sessionData,
    isLoading: isSessionLoading,
    error: sessionError,
  } = useSession(sessionId);

  // Track AI activity to enable faster file polling when AI is working
  const { isActive: isAiActive } = useCurrentAiActivity(sessionId);
  const { data: filesData, isLoading: isFilesLoading } = useSessionFiles(sessionId, isAiActive);

  const session = sessionData?.session;
  const files = filesData?.files || [];

  if (isSessionLoading) {
    return (
      <div className={styles.loadingContainer}>
        <Skeleton style={{ height: '3rem', width: '100%', marginBottom: '1rem' }} />
        <div style={{ display: 'flex', height: 'calc(100vh - 8rem)' }}>
          <Skeleton style={{ width: '250px', height: '100%', marginRight: '1px' }} />
          <Skeleton style={{ flex: 1, height: '100%' }} />
        </div>
      </div>
    );
  }

  if (sessionError || !session) {
    return (
      <div className={styles.errorContainer}>
        <h2>Session not found</h2>
        <p>
          The session you are looking for does not exist or you don&apos;t have permission to view
          it.
        </p>
        <Button
          onClick={() => navigate('/dashboard')}
          title="Back to Dashboard"
          aria-label="Back to Dashboard"
        >
          <Home size={16} />
        </Button>
      </div>
    );
  }

  return (
    <AlternativeRuntimeProvider sessionId={sessionId!}>
      <SessionPageContent
        sessionId={sessionId!}
        session={session}
        files={files}
        isFilesLoading={isFilesLoading}
      />
    </AlternativeRuntimeProvider>
  );
}
